import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Page,
  Card,
  BlockStack,
  InlineStack,
  TextField,
  Button,
  Badge,
  Text,
  Box,
  Divider,
  Banner,
  SkeletonBodyText,
} from '@shopify/polaris';
import { returnsApi } from '@/services';
import { orderNumber } from '@/utils/orderNumber';

export default function ReceiveProduct() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [scanValue, setScanValue] = useState('');
  const [searchValue, setSearchValue] = useState('');
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const { data: scanResult, isLoading, error } = useQuery({
    queryKey: ['return-scan', searchValue],
    queryFn: () => returnsApi.scan(searchValue),
    enabled: searchValue.length > 0,
    retry: false,
  });

  const processReturnMutation = useMutation({
    mutationFn: (dto: { rentalId: string; serialNumbers: string[] }) =>
      returnsApi.processReturn(dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['return-scan', searchValue] });
      setSuccessMessage(`Return processed successfully for ${searchValue}`);
      setScanValue('');
      setSearchValue('');
    },
  });

  const handleScan = () => {
    if (scanValue.trim()) {
      setSearchValue(scanValue.trim());
      setSuccessMessage(null);
    }
  };

  const rental = scanResult?.rental;

  return (
    <Page
      title="Receive Rental Product"
      backAction={{ content: 'Returns', onAction: () => navigate('/returns') }}
    >
      <BlockStack gap="400">
        {successMessage && (
          <Banner tone="success" onDismiss={() => setSuccessMessage(null)}>
            <p>{successMessage}</p>
          </Banner>
        )}

        <Card>
          <BlockStack gap="300">
            <Text as="h2" variant="headingMd">Scan or Enter Serial / Tracking Number</Text>
            <InlineStack gap="300" blockAlign="end">
              <Box minWidth="400px">
                <div onKeyDown={(e) => { if (e.key === 'Enter') handleScan(); }}>
                  <TextField
                    label="Serial or Tracking Number"
                    labelHidden
                    value={scanValue}
                    onChange={setScanValue}
                    autoComplete="off"
                    placeholder="Scan barcode or enter number..."
                  />
                </div>
              </Box>
              <Button variant="primary" onClick={handleScan} loading={isLoading}>
                Look Up
              </Button>
            </InlineStack>
          </BlockStack>
        </Card>

        {error && searchValue && (
          <Banner tone="warning">
            <p>No rental found for "{searchValue}". Check the serial or tracking number.</p>
          </Banner>
        )}

        {isLoading && (
          <Card><SkeletonBodyText lines={6} /></Card>
        )}

        {rental && (rental as any).issueReportedAt && (
          <Banner
            tone="warning"
            title={`⚠ Issue was reported on this rental (${new Date(
              (rental as any).issueReportedAt,
            ).toLocaleDateString()}) — test this equipment before returning it to stock`}
          >
            <p>{(rental as any).issueNotes}</p>
          </Banner>
        )}
        {rental && (
          <Card>
            <BlockStack gap="400">
              <InlineStack align="space-between">
                <Text as="h2" variant="headingMd">
                  Order {orderNumber(rental)}
                </Text>
                <Badge tone={rental.rentalStatus === 'in_use' ? 'success' : rental.rentalStatus === 'overdue' ? 'critical' : 'info'}>
                  {rental.rentalStatus.replace(/_/g, ' ')}
                </Badge>
              </InlineStack>
              <Divider />

              <BlockStack gap="200">
                <InlineStack align="space-between">
                  <Text as="span" tone="subdued">Customer</Text>
                  <Text as="span">{rental.patientName || rental.clinic?.name || 'N/A'}</Text>
                </InlineStack>
                <InlineStack align="space-between">
                  <Text as="span" tone="subdued">Rental Period</Text>
                  <Text as="span">
                    {rental.rentalStartDate ? new Date(rental.rentalStartDate).toLocaleDateString() : '—'} —{' '}
                    {rental.rentalEndDate ? new Date(rental.rentalEndDate).toLocaleDateString() : '—'}
                  </Text>
                </InlineStack>
                <InlineStack align="space-between">
                  <Text as="span" tone="subdued">Items</Text>
                  <Text as="span">
                    {rental.items?.map((i: any) => `${i.productName} (${i.assignedSerials?.join(', ') || 'no serial'})`).join('; ')}
                  </Text>
                </InlineStack>
              </BlockStack>

              <Divider />

              <InlineStack gap="300">
                <Button
                  variant="primary"
                  onClick={() => {
                    const serials = rental.items?.flatMap((i: any) => i.assignedSerials || []) || [];
                    processReturnMutation.mutate({
                      rentalId: rental.id,
                      serialNumbers: serials.length > 0 ? serials : [searchValue],
                    });
                  }}
                  loading={processReturnMutation.isPending}
                  disabled={!['shipped', 'in_use', 'extended', 'overdue'].includes(rental.rentalStatus)}
                >
                  Mark as Received
                </Button>
                <Button onClick={() => navigate(`/rentals/${rental.id}`)}>
                  View Full Details
                </Button>
              </InlineStack>

              {processReturnMutation.isError && (
                <Banner tone="critical">
                  <p>{(processReturnMutation.error as any)?.response?.data?.message || 'Failed to process return'}</p>
                </Banner>
              )}
            </BlockStack>
          </Card>
        )}
      </BlockStack>
    </Page>
  );
}
